import { useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import {
  ArrowLeft, Camera, CameraOff, Lightbulb, Volume2, EyeOff, Smile,
  CheckCircle, Clock, Shield, Video, RotateCcw, Send,
  AlertCircle, AlertTriangle, UploadCloud, WifiOff,
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import VideoRecorder from '../components/cabin/interview/VideoRecorder';
import InterviewQuestionPanel from '../components/cabin/interview/InterviewQuestionPanel';
import { useInterviewSimStart } from '../hooks/cabin/useInterviewSimStart';
import { useInterviewSimSubmit } from '../hooks/cabin/useInterviewSimSubmit';
import { useMediaRecorder } from '../hooks/cabin/useMediaRecorder';
import { interviewAvailabilityQueryKey } from '../hooks/cabin/useInterviewAvailability';

// ─── Step constants ────────────────────────────────────────────────────────────
const STEP_INSTRUCTIONS = 'instructions';
const STEP_COUNTDOWN    = 'countdown';
const STEP_RECORDING    = 'recording';
const STEP_PREVIEW      = 'preview';
const STEP_SUBMITTED    = 'submitted';

// ─── Instruction items ─────────────────────────────────────────────────────────
const INSTRUCTION_ICONS = [Shield, Video, Lightbulb, Volume2, Smile];

function InstructionItem({ icon: Icon, textKey, t }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-xl bg-sky-50 border-2 border-sky-200 flex items-center justify-center shrink-0">
        <Icon size={18} className="text-[var(--brand-primary)]" aria-hidden="true" />
      </div>
      <p className="text-sm text-gray-700 leading-snug pt-1.5">{t(textKey)}</p>
    </div>
  );
}

// ─── Countdown overlay ─────────────────────────────────────────────────────────
function CountdownOverlay({ count }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
      <AnimatePresence mode="wait">
        <motion.div
          key={count}
          initial={{ scale: 1.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.5, opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="text-white font-display font-black"
          style={{ fontSize: 'clamp(80px, 20vw, 160px)' }}
          aria-live="assertive"
          aria-atomic="true"
        >
          {count}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

// ─── Global Timer Pill ─────────────────────────────────────────────────────────
// Replaces RecordingBadge. Shows authoritative remaining time for the interview.
// Colour thresholds: >120s emerald, 61-120s amber, ≤60s rose+pulse.
function GlobalTimerPill({ remaining, t }) {
  const prefersReducedMotion = useReducedMotion();

  const fmtTime = (s) =>
    `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  const safeRemaining = remaining == null ? 0 : remaining;
  const timeStr = fmtTime(safeRemaining);

  let colorCls, bgCls, borderCls, IconCmp;
  if (safeRemaining > 120) {
    colorCls  = 'text-emerald-700';
    bgCls     = 'bg-emerald-50';
    borderCls = 'border-emerald-200';
    IconCmp   = Clock;
  } else if (safeRemaining > 60) {
    colorCls  = 'text-amber-700';
    bgCls     = 'bg-amber-50';
    borderCls = 'border-amber-200';
    IconCmp   = AlertCircle;
  } else {
    colorCls  = 'text-rose-600';
    bgCls     = 'bg-rose-50';
    borderCls = 'border-rose-200';
    IconCmp   = AlertTriangle;
  }

  const isUrgent = safeRemaining <= 60;

  return (
    <motion.div
      className={`flex items-center gap-1.5 ${bgCls} border ${borderCls} rounded-lg px-3 py-1.5 min-w-[72px] justify-center`}
      animate={isUrgent && !prefersReducedMotion ? { scale: [1, 1.06, 1] } : { scale: 1 }}
      transition={isUrgent && !prefersReducedMotion
        ? { duration: 0.7, repeat: Infinity, ease: 'easeInOut' }
        : {}}
      aria-label={t('interviewSim.globalTimer.a11y').replace('{{time}}', timeStr)}
      aria-live="polite"
    >
      <IconCmp size={14} className={colorCls} aria-hidden="true" />
      <span className={`text-xs font-mono tabular-nums font-bold ${colorCls}`}>
        {timeStr}
      </span>
    </motion.div>
  );
}

// ─── Time-Up Modal ─────────────────────────────────────────────────────────────
// Shows when the global timer reaches 0. Cannot be dismissed by clicking overlay.
// state: 'uploading' | 'success' | 'error'
// Focus behaviour: focus moves into the modal on open and on state change.
// Tab cycles only within modal (focus trap). Escape is blocked (non-dismissible).
function TimeUpModal({ state, uploadProgress, onOk, onRetry, retryDisabled, t }) {
  const containerRef = useRef(null);

  // Move focus to first focusable button (or container fallback) on open and on
  // state transitions (e.g. uploading → success/error renders new buttons).
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const btn = container.querySelector('button:not([disabled])');
    if (btn) btn.focus();
    else container.focus();
  }, [state]);

  // Focus trap: Tab cycles within modal; Escape is suppressed (modal is non-dismissible).
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        return;
      }
      if (e.key !== 'Tab') return;
      const focusable = Array.from(
        container.querySelectorAll(
          'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      );
      if (!focusable.length) { e.preventDefault(); return; }
      const first = focusable[0];
      const last  = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last)  { e.preventDefault(); first.focus(); }
      }
    };

    container.addEventListener('keydown', handleKeyDown);
    return () => container.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      role="dialog"
      aria-modal="true"
      aria-labelledby="time-up-modal-title"
    >
      {/* tabIndex={-1} makes the container programmatically focusable as a fallback */}
      <div
        ref={containerRef}
        tabIndex={-1}
        className="bg-white border-2 border-gray-200 rounded-2xl p-6 max-w-sm w-full mx-4 shadow-xl flex flex-col gap-4 items-center text-center outline-none"
      >

        {state === 'uploading' && (
          <>
            <motion.div
              animate={{ y: [0, -4, 0] }}
              transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
            >
              <UploadCloud size={40} className="text-cyan-500" aria-hidden="true" />
            </motion.div>
            <div>
              <h2 id="time-up-modal-title" className="font-display font-black text-xl text-[var(--text-main)] mb-1">
                {t('interviewSim.timeUp.modalTitle')}
              </h2>
              <p className="text-sm text-gray-500 leading-relaxed">
                {t('interviewSim.timeUp.uploadingBody')}
              </p>
            </div>
            <div className="w-full h-2 bg-cyan-200 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-cyan-500 rounded-full"
                animate={{ width: `${uploadProgress || 0}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </>
        )}

        {state === 'success' && (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', damping: 12, stiffness: 200 }}
            >
              <CheckCircle size={40} className="text-emerald-500" aria-hidden="true" />
            </motion.div>
            <div>
              <h2 id="time-up-modal-title" className="font-display font-black text-xl text-[var(--text-main)] mb-1">
                {t('interviewSim.timeUp.modalTitle')}
              </h2>
              <p className="text-sm text-gray-500 leading-relaxed">
                {t('interviewSim.timeUp.modalBody')}
              </p>
            </div>
            <motion.button
              type="button"
              onClick={onOk}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full py-3 rounded-2xl bg-cyan-500 border-2 border-cyan-700 text-white font-bold text-sm shadow-[2px_2px_0px_0px_rgba(0,0,0,0.15)] hover:bg-cyan-600 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] min-h-[44px]"
            >
              {t('interviewSim.timeUp.cta')}
            </motion.button>
          </>
        )}

        {state === 'error' && (
          <>
            <WifiOff size={40} className="text-rose-400" aria-hidden="true" />
            <div>
              <h2 id="time-up-modal-title" className="font-display font-black text-xl text-[var(--text-main)] mb-1">
                {t('interviewSim.timeUp.errorTitle')}
              </h2>
              <p className="text-sm text-gray-500 leading-relaxed">
                {t('interviewSim.timeUp.errorBody')}
              </p>
            </div>
            <motion.button
              type="button"
              onClick={onRetry}
              disabled={retryDisabled}
              whileHover={!retryDisabled ? { scale: 1.02 } : {}}
              whileTap={!retryDisabled ? { scale: 0.98 } : {}}
              className="w-full py-3 rounded-2xl bg-rose-500 border-2 border-rose-700 text-white font-bold text-sm shadow-[2px_2px_0px_0px_rgba(0,0,0,0.15)] hover:bg-rose-600 transition-colors disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] min-h-[44px]"
            >
              {t('interviewSim.timeUp.retryCta')}
            </motion.button>
          </>
        )}

      </div>
    </div>
  );
}

// ─── Camera preview helper ─────────────────────────────────────────────────────
function CameraPreview({ stream }) {
  const videoRef = useRef(null);
  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);
  return (
    <video
      ref={videoRef}
      autoPlay
      muted
      playsInline
      className="w-full h-full object-cover"
      style={{ transform: 'scaleX(-1)' }}
      aria-label="Kamera önizlemesi"
    />
  );
}

/**
 * CabinInterviewSimulation
 * Mülakat Simülasyonu — akış:
 *   talimatlar (kamera önizleme) → [Mülakata Başla → start API] → geri sayım → kayıt → önizleme → gönder
 *
 * Timer mimarisi (v2 — global timer):
 *  - start response'tan expires_at + server_now alınır.
 *  - clock-skew offset: offset = Date.parse(server_now) - Date.now()
 *  - Her saniye: remaining = Math.max(0, round((Date.parse(expires_at) - (Date.now() + offset)) / 1000))
 *  - remaining === 0 → stopRecording() + otomatik submit + TimeUpModal
 *  - Per-soru timer KALDIRILDI → ileri-geri reset bug kökten çözüldü.
 */
export default function CabinInterviewSimulation() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, lang } = useLanguage();
  const toast = useToast();
  const queryClient = useQueryClient();

  // State from hub
  const stateOpts = location.state || {};
  const difficulty = stateOpts.difficulty || 3;
  const language = stateOpts.language || 'tr';

  // Session state
  const [sessionId, setSessionId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [step, setStep] = useState(STEP_INSTRUCTIONS);
  const [countdownVal, setCountdownVal] = useState(5);
  const countdownRef = useRef(null);

  // ── Global timer state ──────────────────────────────────────────────────────
  const [expiresAt, setExpiresAt] = useState(null);
  const [clockOffset, setClockOffset] = useState(0);
  const [remaining, setRemaining] = useState(null);
  const globalTimerRef = useRef(null);

  // ── Auto-submit state (fires when remaining === 0) ──────────────────────────
  // null | 'uploading' | 'success' | 'error'
  const [timeUpModalState, setTimeUpModalState] = useState(null);
  // Ref guard: prevents double-fire if remaining hits 0 while submit is in flight
  const autoSubmitFiredRef = useRef(false);
  // Ref guard: prevents auto-submit from overriding a manual stop (and vice-versa).
  // Whichever handler runs first sets this; the other becomes a no-op.
  const stopRequestedRef = useRef(false);

  // Preview stream state (instructions step only)
  const [previewStream, setPreviewStream] = useState(null);
  const [previewDenied, setPreviewDenied] = useState(false);
  const [previewChecking, setPreviewChecking] = useState(true);
  const previewStreamRef = useRef(null);

  // Hooks
  const { start, loading: startLoading } = useInterviewSimStart();
  const { submit, loading: submitLoading, progress: uploadProgress } = useInterviewSimSubmit();

  const {
    isRecording,
    blob: videoBlob,
    blobUrl: videoBlobUrl,
    duration: recordingDuration,
    permissionDenied,
    error: recorderError,
    start: startRecording,
    stop: stopRecording,
    reset: resetRecording,
    streamRef,
  } = useMediaRecorder('video');

  // ── Preview stream cleanup helper ───────────────────────────────────────────
  const stopPreviewStream = useCallback(() => {
    if (previewStreamRef.current) {
      previewStreamRef.current.getTracks().forEach(tr => tr.stop());
      previewStreamRef.current = null;
    }
    setPreviewStream(null);
  }, []);

  // ── Start preview stream on instructions mount ──────────────────────────────
  useEffect(() => {
    let cancelled = false;

    async function openPreview() {
      setPreviewChecking(true);
      setPreviewDenied(false);
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        if (cancelled) {
          stream.getTracks().forEach(tr => tr.stop());
          return;
        }
        previewStreamRef.current = stream;
        setPreviewStream(stream);
      } catch {
        if (!cancelled) {
          setPreviewDenied(true);
        }
      } finally {
        if (!cancelled) setPreviewChecking(false);
      }
    }

    if (step === STEP_INSTRUCTIONS) {
      openPreview();
    }

    return () => {
      cancelled = true;
    };
  // Only run when entering instructions step
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step === STEP_INSTRUCTIONS ? 'instructions' : step]);

  // ── Stop preview when leaving instructions ──────────────────────────────────
  useEffect(() => {
    if (step !== STEP_INSTRUCTIONS) {
      stopPreviewStream();
    }
  }, [step, stopPreviewStream]);

  // ── Cleanup on unmount ──────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      stopPreviewStream();
      clearTimeout(countdownRef.current);
      clearInterval(globalTimerRef.current);
    };
  }, [stopPreviewStream]);

  // ── Global timer tick — only during STEP_RECORDING ────────────────────────
  useEffect(() => {
    if (step !== STEP_RECORDING || !expiresAt) return;

    globalTimerRef.current = setInterval(() => {
      const r = Math.max(
        0,
        Math.round((Date.parse(expiresAt) - (Date.now() + clockOffset)) / 1000)
      );
      setRemaining(r);
    }, 1000);

    return () => clearInterval(globalTimerRef.current);
  }, [step, expiresAt, clockOffset]);

  // ── Auto-submit when remaining hits 0 ─────────────────────────────────────
  useEffect(() => {
    if (remaining !== 0 || step !== STEP_RECORDING || autoSubmitFiredRef.current) return;
    // If manual stop already claimed ownership of the stop, do not override
    if (stopRequestedRef.current) return;
    autoSubmitFiredRef.current = true;
    clearInterval(globalTimerRef.current);
    setTimeUpModalState('uploading');
    stopRecording(); // blob will be set asynchronously via recorder.onstop
  // stopRecording is stable (useCallback with no deps)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remaining, step]);

  // ── When blob is ready after auto-submit trigger ───────────────────────────
  useEffect(() => {
    if (!autoSubmitFiredRef.current || !videoBlob || !sessionId || submitLoading) return;
    // Reset flag immediately to prevent double-fire on subsequent renders
    autoSubmitFiredRef.current = false;

    const doAutoSubmit = async () => {
      const ext = (videoBlob.type || '').includes('mp4') ? 'mp4' : 'webm';
      const result = await submit({
        sessionId,
        videoBlob,
        mimeType: videoBlob.type || `video/${ext}`,
      });
      if (result) {
        setTimeUpModalState('success');
      } else {
        setTimeUpModalState('error');
      }
    };

    doAutoSubmit();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoBlob, sessionId]);

  // ── Step: "Mülakata Başla" → call start API → countdown ────────────────────
  const handleStartInterview = useCallback(async () => {
    if (startLoading) return;

    let data = null;

    if (!sessionId) {
      data = await start({ difficulty, language });
      // Cooldown sentinel: 429 response, user must wait
      if (data?.__cooldown) {
        const nextDate = data.next_available_at
          ? new Intl.DateTimeFormat(lang === 'tr' ? 'tr-TR' : 'en-GB', {
              day: 'numeric', month: 'long', year: 'numeric',
            }).format(new Date(data.next_available_at))
          : '';
        toast?.warning(
          t('interview.sim.cooldown.toast').replace('{{date}}', nextDate)
        );
        queryClient.invalidateQueries({ queryKey: interviewAvailabilityQueryKey });
        return;
      }
      if (!data) {
        toast?.error(t('interview.sim.start_error'));
        return;
      }
      setSessionId(data.session_id);
      setQuestions(data.questions || []);

      // ── Authoritative global timer init ──────────────────────────────────
      // Clock-skew: offset = server_now - client_now; compensates for drifted clocks.
      if (data.expires_at && data.server_now) {
        const offset = Date.parse(data.server_now) - Date.now();
        setClockOffset(offset);
        setExpiresAt(data.expires_at);
        const initialRemaining = Math.max(
          0,
          Math.round((Date.parse(data.expires_at) - (Date.now() + offset)) / 1000)
        );
        setRemaining(initialRemaining);
      } else if (data.total_duration_seconds) {
        // Fallback: backend didn't send expires_at (older deploy)
        const fallbackExpires = new Date(Date.now() + data.total_duration_seconds * 1000).toISOString();
        setClockOffset(0);
        setExpiresAt(fallbackExpires);
        setRemaining(data.total_duration_seconds);
      }
    }

    // Hand off preview stream to recorder's streamRef so camera doesn't reopen
    if (previewStreamRef.current) {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(tr => tr.stop());
      }
      streamRef.current = previewStreamRef.current;
      previewStreamRef.current = null;
      setPreviewStream(null);
    }

    setCurrentQuestionIndex(0);
    setStep(STEP_COUNTDOWN);
    setCountdownVal(5);
  }, [startLoading, sessionId, start, difficulty, language, lang, toast, t, streamRef, queryClient]);

  // ── Countdown effect ───────────────────────────────────────────────────────
  useEffect(() => {
    if (step !== STEP_COUNTDOWN) return;
    if (countdownVal <= 0) {
      startRecording();
      setStep(STEP_RECORDING);
      return;
    }
    countdownRef.current = setTimeout(() => {
      setCountdownVal(v => v - 1);
    }, 1000);
    return () => clearTimeout(countdownRef.current);
  }, [step, countdownVal, startRecording]);

  // ── Stop recording → preview ───────────────────────────────────────────────
  const handleStopRecording = useCallback(() => {
    // If auto-submit already claimed ownership, manual stop is a no-op
    if (autoSubmitFiredRef.current) return;
    // Claim ownership so the auto-submit effect becomes a no-op if it fires next
    stopRequestedRef.current = true;
    stopRecording();
    setStep(STEP_PREVIEW);
  }, [stopRecording]);

  // ── Retake ─────────────────────────────────────────────────────────────────
  const handleRetake = useCallback(() => {
    resetRecording();
    setStep(STEP_COUNTDOWN);
    setCountdownVal(5);
  }, [resetRecording]);

  // ── Manual Submit (from preview step) ─────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    if (!videoBlob || !sessionId || submitLoading) return;
    const ext = (videoBlob.type || '').includes('mp4') ? 'mp4' : 'webm';
    const result = await submit({ sessionId, videoBlob, mimeType: videoBlob.type || `video/${ext}` });
    if (!result) {
      toast?.error(t('interview.sim.submit_error'));
      return;
    }
    setStep(STEP_SUBMITTED);
  }, [videoBlob, sessionId, submitLoading, submit, toast, t]);

  // ── Auto-submit retry (from error state in TimeUpModal) ────────────────────
  const handleAutoSubmitRetry = useCallback(async () => {
    // Guard against rapid double-tap / concurrent submit
    if (!videoBlob || !sessionId || submitLoading) return;
    setTimeUpModalState('uploading');
    const ext = (videoBlob.type || '').includes('mp4') ? 'mp4' : 'webm';
    const result = await submit({ sessionId, videoBlob, mimeType: videoBlob.type || `video/${ext}` });
    if (result) {
      setTimeUpModalState('success');
    } else {
      setTimeUpModalState('error');
    }
  }, [videoBlob, sessionId, submitLoading, submit]);

  // ── TimeUpModal OK → go to submitted step ─────────────────────────────────
  const handleTimeUpOk = useCallback(() => {
    setTimeUpModalState(null);
    setStep(STEP_SUBMITTED);
  }, []);

  // ── Back button handler ────────────────────────────────────────────────────
  const handleBack = useCallback(() => {
    stopPreviewStream();
    resetRecording();
    navigate('/programs/kabin-memurlugu/interview');
  }, [stopPreviewStream, resetRecording, navigate]);

  // ── Question navigation callbacks ──────────────────────────────────────────
  const handleNextQuestion = useCallback(
    () => setCurrentQuestionIndex((i) => i + 1),
    []
  );
  const handlePrevQuestion = useCallback(
    () => setCurrentQuestionIndex((i) => Math.max(0, i - 1)),
    []
  );

  // ── Reduce motion ─────────────────────────────────────────────────────────
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ─── Derived: tüm sorular tamamlandı mı? ──────────────────────────────────
  const allDone = questions.length > 0 && currentQuestionIndex >= questions.length;

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <div className={`bg-[var(--bg-app)] flex flex-col ${step === STEP_RECORDING ? 'h-screen overflow-hidden' : 'min-h-screen'}`}>

      {/* Time-Up Modal — rendered outside scroll container, above everything */}
      {timeUpModalState && (
        <TimeUpModal
          state={timeUpModalState}
          uploadProgress={uploadProgress}
          onOk={handleTimeUpOk}
          onRetry={handleAutoSubmitRetry}
          retryDisabled={submitLoading}
          t={t}
        />
      )}

      {/* Top bar */}
      <div className="sticky top-0 z-10 bg-white border-b-2 border-gray-200 px-4 py-3 flex items-center gap-3">
        <button
          type="button"
          onClick={handleBack}
          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] min-w-[44px] min-h-[44px] flex items-center justify-center"
          aria-label={t('cabin.interview.text.back')}
        >
          <ArrowLeft size={18} aria-hidden="true" />
        </button>
        <h1 className="font-display text-base font-black text-[var(--text-main)] flex-1 leading-none">
          {t('interview.sim.title')}
        </h1>
        {/* Global timer replaces elapsed RecordingBadge */}
        {step === STEP_RECORDING && remaining != null && (
          <GlobalTimerPill remaining={remaining} t={t} />
        )}
      </div>

      {/* Countdown overlay */}
      {step === STEP_COUNTDOWN && !prefersReducedMotion && (
        <CountdownOverlay count={countdownVal} />
      )}
      {step === STEP_COUNTDOWN && prefersReducedMotion && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
          <p className="text-white text-6xl font-black" aria-live="assertive">{countdownVal}</p>
        </div>
      )}

      {/* Main content */}
      <div className={`flex-1 mx-auto w-full ${
        step === STEP_RECORDING
          ? 'max-w-[1400px] overflow-hidden flex flex-col px-4 md:px-6 py-4 md:py-5 gap-4'
          : 'max-w-2xl overflow-y-auto p-4 md:p-6'
      }`}>
        <AnimatePresence mode="wait">

          {/* ── INSTRUCTIONS ── */}
          {step === STEP_INSTRUCTIONS && (
            <motion.div
              key="instructions"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.3 }}
              className="flex flex-col gap-5"
            >
              <div>
                <h2 className="font-display text-xl font-black text-[var(--text-main)] mb-1">
                  {t('interview.sim.instructions.title')}
                </h2>
                <p className="text-sm text-gray-500">
                  {t('interview.sim.instructions.subtitle')}
                </p>
              </div>

              {/* ── Camera preview panel ── */}
              <div className="bg-gray-900 rounded-2xl overflow-hidden border-2 border-gray-700 aspect-video relative">
                {previewChecking && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                    <div
                      className="w-8 h-8 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin"
                      aria-hidden="true"
                    />
                    <p className="text-gray-300 text-sm">{t('interview.sim.camera_checking')}</p>
                  </div>
                )}

                {!previewChecking && previewDenied && (
                  <div
                    className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center"
                    role="alert"
                    aria-live="assertive"
                  >
                    <div className="w-12 h-12 rounded-2xl bg-rose-900/60 border-2 border-rose-700 flex items-center justify-center">
                      <CameraOff size={22} className="text-rose-400" aria-hidden="true" />
                    </div>
                    <p className="text-rose-300 text-sm leading-relaxed max-w-xs">
                      {t('interview.sim.camera_denied')}
                    </p>
                  </div>
                )}

                {!previewChecking && !previewDenied && previewStream && (
                  <CameraPreview stream={previewStream} />
                )}

                {!previewChecking && !previewDenied && previewStream && (
                  <div
                    className="absolute bottom-2 left-2 flex items-center gap-1.5 bg-black/50 rounded-lg px-2 py-1"
                    aria-label={t('interview.sim.camera_ready')}
                  >
                    <Camera size={12} className="text-green-400" aria-hidden="true" />
                    <span className="text-white text-xs">{t('interview.sim.camera_ready')}</span>
                  </div>
                )}
              </div>

              {/* Instructions list */}
              <div className="bg-white border-2 border-gray-200 rounded-2xl p-5 flex flex-col gap-4">
                <InstructionItem icon={INSTRUCTION_ICONS[0]} textKey="interview.sim.instructions.dress"    t={t} />
                <InstructionItem icon={INSTRUCTION_ICONS[1]} textKey="interview.sim.instructions.camera"   t={t} />
                <InstructionItem icon={INSTRUCTION_ICONS[2]} textKey="interview.sim.instructions.lighting" t={t} />
                <InstructionItem icon={INSTRUCTION_ICONS[3]} textKey="interview.sim.instructions.quiet"    t={t} />
                <InstructionItem icon={INSTRUCTION_ICONS[4]} textKey="interview.sim.instructions.natural"  t={t} />
              </div>

              {/* Flow info */}
              <div className="bg-sky-50 border-2 border-sky-200 rounded-2xl p-4 flex items-start gap-3">
                <Video size={18} className="text-[var(--brand-primary)] shrink-0 mt-0.5" aria-hidden="true" />
                <p className="text-xs text-[var(--brand-primary)] leading-relaxed font-medium">
                  {t('interview.sim.flow_note')}
                </p>
              </div>

              {/* KVKK notice */}
              <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-4 flex items-start gap-3">
                <Shield size={18} className="text-amber-600 shrink-0 mt-0.5" aria-hidden="true" />
                <p className="text-xs text-amber-800 leading-relaxed">
                  {t('interview.sim.kvkk_notice')}
                </p>
              </div>

              <motion.button
                type="button"
                onClick={handleStartInterview}
                disabled={startLoading || previewDenied || previewChecking}
                whileHover={!startLoading && !previewDenied && !previewChecking ? { scale: 1.02 } : {}}
                whileTap={!startLoading && !previewDenied && !previewChecking ? { scale: 0.98 } : {}}
                className="w-full py-4 rounded-2xl bg-cyan-500 border-2 border-cyan-700 text-white font-bold text-base shadow-[2px_2px_0px_0px_rgba(0,0,0,0.15)] disabled:opacity-50 hover:bg-cyan-600 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] min-h-[44px] flex items-center justify-center gap-2"
                aria-disabled={startLoading || previewDenied || previewChecking}
              >
                {startLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" aria-hidden="true" />
                ) : (
                  <Video size={20} aria-hidden="true" />
                )}
                {t('interview.sim.start_interview')}
              </motion.button>
            </motion.div>
          )}

          {/* ── RECORDING ── */}
          {step === STEP_RECORDING && (
            <motion.div
              key="recording"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.3 }}
              className="flex flex-col md:flex-row gap-4 flex-1 min-h-0"
            >
              {/* ── SOL: Kamera alanı ── */}
              <div className="flex-1 min-w-0 flex flex-col gap-2 min-h-0">
                <div className="bg-gray-900 border-2 border-gray-700 rounded-2xl overflow-hidden
                                flex flex-col min-h-0
                                max-h-[45vh] md:max-h-[calc(100vh-160px)]">
                  <VideoRecorder
                    isRecording={isRecording}
                    blobUrl={null}
                    duration={recordingDuration}
                    permissionDenied={permissionDenied}
                    error={recorderError}
                    stream={streamRef.current}
                    onStart={() => {}}
                    onStop={handleStopRecording}
                    onReset={resetRecording}
                    labelRecord={t('cabin.interview.video.record')}
                    labelStop={t('interview.sim.finish_recording')}
                    labelRetry={t('interview.sim.retake')}
                    labelPermDenied={t('cabin.interview.video.perm_denied')}
                    hideControls={true}
                    videoClassName="h-full"
                  />
                </div>
              </div>

              {/* ── SAĞ: Soru paneli + buton ── */}
              <div className="w-full md:w-[340px] shrink-0 flex flex-col gap-3">
                {/* Language badge */}
                <div className="shrink-0">
                  <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-sky-50 border-2 border-sky-200 text-[var(--brand-primary)]">
                    {t('interview.sim.question_lang_label')}:{' '}
                    {language === 'tr'
                      ? (t('kariyer.lang.tr') || 'Türkçe')
                      : (t('kariyer.lang.en') || 'English')}
                  </span>
                </div>

                {/* Questions panel */}
                <div className="flex-1 min-h-0 overflow-y-auto">
                  <InterviewQuestionPanel
                    questions={questions}
                    currentIndex={currentQuestionIndex}
                    onNext={handleNextQuestion}
                    onPrev={handlePrevQuestion}
                    onFinish={handleStopRecording}
                    isLoading={false}
                    isError={false}
                    onBack={handleBack}
                  />
                </div>

                {/* "Kaydı Bitir" butonu — only while questions active */}
                {!allDone && questions.length > 0 && (
                  <motion.button
                    type="button"
                    onClick={handleStopRecording}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="shrink-0 w-full py-3 rounded-2xl bg-rose-500 border-2 border-rose-700 text-white font-bold text-sm shadow-[2px_2px_0px_0px_rgba(0,0,0,0.15)] hover:bg-rose-600 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] min-h-[44px] flex items-center justify-center gap-2"
                    aria-label={t('interview.sim.finish_recording')}
                  >
                    <EyeOff size={18} aria-hidden="true" />
                    {t('interview.sim.finish_recording')}
                  </motion.button>
                )}
              </div>
            </motion.div>
          )}

          {/* ── PREVIEW ── */}
          {step === STEP_PREVIEW && (
            <motion.div
              key="preview"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.3 }}
              className="flex flex-col gap-5"
            >
              <div>
                <h2 className="font-display text-lg font-black text-[var(--text-main)] mb-1">
                  {t('interview.sim.preview_title')}
                </h2>
                <p className="text-sm text-gray-500">{t('interview.sim.preview_subtitle')}</p>
              </div>

              {/* Video playback */}
              <div className="bg-white border-2 border-gray-200 rounded-2xl p-4">
                <VideoRecorder
                  isRecording={false}
                  blobUrl={videoBlobUrl}
                  duration={recordingDuration}
                  permissionDenied={false}
                  error={null}
                  stream={null}
                  onStart={() => {}}
                  onStop={() => {}}
                  onReset={handleRetake}
                  labelRecord={t('cabin.interview.video.record')}
                  labelStop={t('cabin.interview.video.stop')}
                  labelRetry={t('interview.sim.retake')}
                  labelPermDenied=""
                  hideControls={true}
                />
              </div>

              {/* Duration info */}
              <div className="flex items-center gap-2 bg-gray-50 border-2 border-gray-200 rounded-xl px-4 py-3">
                <Clock size={16} className="text-gray-400" aria-hidden="true" />
                <span className="text-sm text-gray-600">
                  {t('cabin.interview.history.duration')}:{' '}
                  <strong>
                    {String(Math.floor(recordingDuration / 60)).padStart(2, '0')}:
                    {String(recordingDuration % 60).padStart(2, '0')}
                  </strong>
                </span>
              </div>

              {/* Upload progress */}
              {submitLoading && (
                <div className="bg-cyan-50 border-2 border-cyan-200 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-cyan-700">{t('cabin.interview.video.uploading')}</span>
                    <span className="text-xs font-bold text-cyan-700">{uploadProgress}%</span>
                  </div>
                  <div className="h-2 bg-cyan-200 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-cyan-500 rounded-full"
                      animate={{ width: `${uploadProgress}%` }}
                      transition={{ duration: 0.3 }}
                    />
                  </div>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-3">
                <motion.button
                  type="button"
                  onClick={handleRetake}
                  disabled={submitLoading}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex-1 py-3 rounded-2xl bg-white border-2 border-gray-300 text-gray-700 font-bold text-sm hover:bg-gray-50 transition-colors disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] min-h-[44px] flex items-center justify-center gap-2"
                >
                  <RotateCcw size={16} aria-hidden="true" />
                  {t('interview.sim.retake')}
                </motion.button>
                <motion.button
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitLoading || !videoBlob}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex-[2] py-3 rounded-2xl bg-cyan-500 border-2 border-cyan-700 text-white font-bold text-sm shadow-[2px_2px_0px_0px_rgba(0,0,0,0.15)] disabled:opacity-50 hover:bg-cyan-600 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] min-h-[44px] flex items-center justify-center gap-2"
                >
                  <Send size={16} aria-hidden="true" />
                  {submitLoading ? t('cabin.interview.video.uploading') : t('interview.sim.submit')}
                </motion.button>
              </div>
            </motion.div>
          )}

          {/* ── SUBMITTED ── */}
          {step === STEP_SUBMITTED && (
            <motion.div
              key="submitted"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="flex flex-col items-center gap-6 py-10 text-center"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', damping: 12, stiffness: 200, delay: 0.1 }}
                className="w-20 h-20 rounded-2xl bg-emerald-100 border-2 border-emerald-300 flex items-center justify-center"
              >
                <CheckCircle size={40} className="text-emerald-500" aria-hidden="true" />
              </motion.div>

              <div>
                <h2 className="font-display text-2xl font-black text-[var(--text-main)] mb-2">
                  {t('interview.sim.submitted_title')}
                </h2>
                <p className="text-sm text-gray-500 max-w-sm mx-auto leading-relaxed">
                  {t('interview.sim.waiting_review')}
                </p>
              </div>

              <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-4 max-w-sm w-full text-left">
                <div className="flex items-center gap-2 mb-2">
                  <Clock size={16} className="text-amber-600" aria-hidden="true" />
                  <p className="text-xs font-bold text-amber-700 uppercase tracking-wide">
                    {t('interview.sim.pending')}
                  </p>
                </div>
                <p className="text-xs text-amber-800 leading-relaxed">
                  {t('interview.sim.pending_desc')}
                </p>
              </div>

              <motion.button
                type="button"
                onClick={() => navigate('/programs/kabin-memurlugu/interview/history')}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="px-8 py-3 rounded-2xl bg-cyan-500 border-2 border-cyan-700 text-white font-bold text-sm shadow-[2px_2px_0px_0px_rgba(0,0,0,0.15)] hover:bg-cyan-600 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] min-h-[44px]"
              >
                {t('interview.sim.view_history')}
              </motion.button>
            </motion.div>
          )}

        </AnimatePresence>
      </div>
    </div>
  );
}
